import React, { useState, useRef, useEffect } from 'react';
import './InlineEditCell.css';

interface InlineEditCellProps {
  value: number | null;
  isEditing: boolean;
  isPending: boolean;
  disabled?: boolean;
  finishNotAvailable?: boolean;  // True when this finish doesn't exist for the card
  onStartEdit: () => void;
  onChange: (value: number | null) => void;
  onSave: () => void;
  onCancel: () => void;
}

const InlineEditCell: React.FC<InlineEditCellProps> = ({
  value,
  isEditing,
  isPending,
  disabled = false,
  finishNotAvailable = false,
  onStartEdit,
  onChange,
  onSave,
  onCancel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(value?.toString() || '');
  const [error, setError] = useState<string | null>(null);

  // Sync local value when prop value changes
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value?.toString() || '');
    }
  }, [value, isEditing]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const validate = (val: string): boolean => {
    // Empty is allowed (represents null price)
    if (!val || val.trim() === '') {
      setError(null);
      return true;
    }

    const num = parseInt(val, 10);

    if (isNaN(num)) {
      setError('Invalid number');
      return false;
    }

    if (num < 0) {
      setError('Must be positive');
      return false;
    }

    if (num > 5000000) {
      setError('Max 5,000,000');
      return false;
    }

    setError(null);
    return true;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (validate(localValue)) {
        onChange(localValue ? parseInt(localValue, 10) : null);
        onSave();
      }
    }

    if (e.key === 'Escape') {
      setLocalValue(value?.toString() || '');
      setError(null);
      onCancel();
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      if (validate(localValue)) {
        onChange(localValue ? parseInt(localValue, 10) : null);
        onSave();
      }
    }
  };

  const handleBlur = () => {
    if (validate(localValue)) {
      onChange(localValue ? parseInt(localValue, 10) : null);
      onSave();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow only digits for whole number point values
    if (/^\d*$/.test(val)) {
      setLocalValue(val);
      // Validate on change for immediate feedback
      validate(val);
    }
  };

  // Finish not available state (card doesn't come in this finish)
  if (finishNotAvailable) {
    return (
      <div className="inline-edit-cell not-available" title="This finish is not available for this card">
        <span className="cell-value">✕</span>
      </div>
    );
  }

  // Disabled state (for other reasons)
  if (disabled) {
    return (
      <div className="inline-edit-cell disabled">
        <span className="cell-value">—</span>
      </div>
    );
  }

  // Editing state
  if (isEditing) {
    return (
      <div className={`inline-edit-cell editing ${error ? 'has-error' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          className="edit-input"
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder="0"
        />
        {error && <div className="edit-error">{error}</div>}
      </div>
    );
  }

  // Display state
  return (
    <div
      className={`inline-edit-cell ${isPending ? 'pending' : ''} ${value == null ? 'missing' : ''}`}
      onClick={onStartEdit}
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onStartEdit()}
      role="button"
      aria-label={`Edit price: ${value != null ? `${Math.round(value).toLocaleString()} pts` : 'not set'}`}
    >
      <span className="cell-value">
        {value != null ? `${Math.round(value).toLocaleString()}` : <span className="missing-price">—</span>}
      </span>
      {isPending && <span className="pending-indicator" title="Unsaved changes">*</span>}
    </div>
  );
};

export default InlineEditCell;
